// Single JS source for the comparison-widget "you overpaid by $X" total and the
// percent-markup figure. SchemaWidget.jsx (the human React page) imports these
// instead of recomputing them inline.
//
// CROSS-RUNTIME TWIN — app/helpers/static_funnel_helper.rb implements the same
// two formulas in Ruby for the <noscript>/AI twin. The two are NOT kept in sync
// by hand: spec/helpers/comparison_widget_total_parity_spec.rb EXECUTES this
// module and asserts it agrees with the Ruby helper over shared fixtures
// (mutation-tested). Change a formula on either side and that spec goes red, so
// the crawler/AI total can never silently diverge from the human total — the
// whole point of the noscript parity. (You can't parse an algorithm for
// equivalence the way funnel_icons_spec parses a literal map, so the drift guard
// runs both implementations rather than comparing source.)
//
// The recipe contract these read is config/schemas/comparison_widget_schema.json.

export function computeTotal(schema, data) {
  const { fields, display } = schema
  if (display.total_formula === "right_value_field") {
    return Number(data[display.right_value_field]) || 0
  }
  // sum_all_currency: every currency field + currency line-item amounts
  let total = 0
  for (const field of fields) {
    if (field.type === "currency") {
      total += Number(data[field.key]) || 0
    } else if (field.type === "line_items" && Array.isArray(data[field.key])) {
      const currencyKeys = (field.item_fields || [])
        .filter((f) => f.type === "currency")
        .map((f) => f.key)
      for (const item of data[field.key]) {
        for (const key of currencyKeys) total += Number(item[key]) || 0
      }
    }
  }
  return total
}

// The ordered breakdown rows between the advertised value and the total.
//
// Extracted out of SchemaWidget.jsx (act:829d77da) so the Ruby noscript twin has
// something to be compared AGAINST. It was inline in the React component, which
// is exactly why the drift guard could only cover the total: the total lived in
// a shared module and the ROWS did not, so the two runtimes were free to print
// different breakdowns while agreeing perfectly on the number. They did — the
// Ruby side omitted standalone currency fields (`tax`), and three live pages
// showed crawlers a breakdown that could not be added up.
//
// Returns RAW amounts, not formatted strings. Formatting is per-runtime
// presentation; the row SET and ORDER is the cross-runtime contract, and that is
// the thing that actually drifted.
//
// `kind` ("item" | "value") exists so the React page can keep styling the two
// passes differently, exactly as it did when it selected them itself. Without
// it this extraction would have quietly restyled every live widget — a visual
// change smuggled in under a correctness fix.
export function breakdownRows(schema, data) {
  const { fields, display } = schema
  const rows = []

  for (const field of fields) {
    if (field.type !== "line_items") continue
    const items = data[field.key]
    if (!Array.isArray(items)) continue
    const labelKey = (field.item_fields || []).find((f) => f.type === "text")?.key
    // Currency first, number only as a fallback — see the Ruby twin's comment.
    // computeTotal counts currency item fields ONLY, so preferring a `number`
    // sibling prints a quantity where the money belongs.
    const itemFields = field.item_fields || []
    const amountKey = (
      itemFields.find((f) => f.type === "currency") ?? itemFields.find((f) => f.type === "number")
    )?.key
    for (const item of items) {
      rows.push({
        label: labelKey ? item[labelKey] : null,
        amount: Number(amountKey ? item[amountKey] : 0) || 0,
        kind: "item",
      })
    }
  }

  // Standalone value fields that are neither big-card figure — `tax` today.
  // Zero is skipped so a recipe declaring the field and a campaign leaving it
  // blank still prints a clean breakdown instead of a "+$0.00" row.
  for (const field of fields) {
    if (field.type !== "currency" && field.type !== "number") continue
    if (field.key === display.left_value_field || field.key === display.right_value_field) continue
    const value = Number(data[field.key]) || 0
    if (value === 0) continue
    rows.push({ label: field.label, amount: value, kind: "value" })
  }

  return rows
}

export function percentMarkup(schema, data) {
  const leftValue = Number(data[schema.display.left_value_field]) || 0
  if (leftValue <= 0) return 0
  return Math.round(((computeTotal(schema, data) - leftValue) / leftValue) * 100)
}
